/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useMemo,
} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ReviewRequest, ReviewStatus, Destination } from '@marageti/z4-sdk/lib/travel';
import { useApiClient, useFetchImmutable } from '@marageti/z4-lib';
import FinalPage from './final-page';
import useTripMatchReview from '../../hooks/use-trip-match-review';
import StepPage from './step-page';
import Step1 from './step-1';
import Step2 from './step-2';

// Trip Review is a multi-step form that allows the user to create a review for a trip match.
// The form is split into 4 steps, each step is a separate component.
// The `TripReview` component is responsible for rendering the correct step based on the current step number.
// The `TripReview` component also handles the logic for saving and submitting the review.
const TripReview = ({
  tripMatchId,
  reviewId = undefined,
}: {
  tripMatchId: string | undefined,
  reviewId: string | undefined
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiClient = useApiClient();
  const draftId = reviewId || searchParams.get('draft');
  const currentStep = Number(searchParams.get('step')) || 1;

  const {
    data: destinationsList, isLoading: isDestinationsListLoading, error: destinationsListError,
  } = useFetchImmutable<Destination[]>(apiClient.destinationsClient.getDestinations, [{ params: { all: true, served: true } }], true);
  const sortedDestinationsList = useMemo(() => destinationsList?.sort((a, b) => a.name.localeCompare(b.name)) || [], [destinationsList]);

  const {
    isLoading,
    submitError,
    setSubmitError,
    submit,
    update,
    existingReview,
    tripMatch,
  } = useTripMatchReview({
    reviewId: draftId || '',
    tripMatchId,
  });

  // Read the rating param once
  const initialRatingFromUrl = useMemo(() => {
    const ratingParam = searchParams.get('rating');
    if (ratingParam) {
      const initialRating = parseInt(ratingParam, 10);
      if (!Number.isNaN(initialRating) && initialRating >= 1 && initialRating <= 5) {
        return initialRating;
      }
    }
    return 0; // Default if not present or invalid
  }, [searchParams]);

  const formValues = useMemo(() => {
    // Prioritize existing review rating, then URL param, then 0
    const initialRating = existingReview?.rating ? (existingReview.rating / 20)
      : initialRatingFromUrl;

    return {
      mainReview: existingReview?.mainReview || '',
      bestParts: existingReview?.bestParts || '',
      doDifferently: existingReview?.doDifferently || '',
      nextDestinations: existingReview?.nextDestinations || [],
      findZicasso: existingReview?.findZicasso || '',
      rating: initialRating,
    };
  }, [existingReview, initialRatingFromUrl]);

  const saveReview = async (
    values: ReviewRequest,
    {
      mode = 'update',
      onSuccess,
    }:
    {
      mode: 'update' | 'submit';
      onSuccess?: (data: any) => void
    },
  ) => {
    const reviewExists = draftId;
    const reviewRequest = {
      mainReview: values.mainReview,
      bestParts: values.bestParts,
      doDifferently: values.doDifferently,
      nextDestinations: values.nextDestinations,
      findZicasso: values.findZicasso,
      rating: (values.rating || 0) * 20,
      status: mode === 'update' ? ReviewStatus.ReviewStatusPending : ReviewStatus.ReviewStatusCompleted,
    };

    if (reviewExists) {
      await update(reviewRequest, { onSuccess });
    } else {
      await submit(reviewRequest, { onSuccess });
    }
  };

  const goToStep = (
    nextOrPrevious: 'next' | 'previous',
    { reviewId: reviewIdParam }: { reviewId?: string | undefined } = {},
  ) => {
    const pageToGoTo = nextOrPrevious === 'next' ? currentStep + 1 : currentStep - 1 || 1;
    const draftIdSearchParam = draftId || reviewIdParam;
    navigate({
      search: draftIdSearchParam ? `?step=${pageToGoTo}&draft=${draftIdSearchParam}` : `?step=${pageToGoTo}`,
    });
  };

  const handleBack = () => {
    goToStep('previous');
  };

  const handleNext = async (data: any) => {
    setSubmitError(null);

    await saveReview(
      {
        ...formValues,
        ...data,
      },
      {
        mode: 'update',
        onSuccess: (review: any) => {
          goToStep('next', {
            reviewId: review?.id,
          });
        },
      },
    );
  };

  const handleSaveForLater = async () => {
    await saveReview(formValues, {
      mode: 'update',
      onSuccess: () => {
        if (tripMatch) {
          navigate({
            pathname: `/trip-requests/${tripMatch.tripRequestId}`,
          });
        } else {
          navigate({
            pathname: '/',
          });
        }
      },
    });
  };

  const handleSubmitReview = async () => {
    await saveReview(formValues, {
      mode: 'submit',
      onSuccess: () => {
        navigate({
          pathname: `/trip-matches/${tripMatchId}/review/${draftId}/submit`,
        });
      },
    });
  };

  const navigateBack = () => {
    if (tripMatch?.tripRequestId) {
      navigate(`/trip-requests/${tripMatch.tripRequestId}`);
    }
  };

  if (isLoading) {
    return <StepPage.Skeleton />;
  }

  switch (currentStep) {
    case 3:
      return (
        <FinalPage
          subtitle={tripMatch?.partnerName}
          formValues={formValues}
          onSaveForLater={handleSaveForLater}
          onSubmitReview={handleSubmitReview}
          error={submitError}
        />
      );

    case 2:
      return (
        <Step2
          activeStep={currentStep}
          defaultValues={formValues}
          onBack={handleBack}
          onNext={handleNext}
          tripMatch={tripMatch}
          error={submitError}
          destinationsList={sortedDestinationsList}
          destinationsListError={destinationsListError}
          destinationsListIsLoading={isDestinationsListLoading}
        />
      );
    default:
      return (
        <Step1
          activeStep={currentStep}
          defaultValues={formValues}
          onNext={handleNext}
          error={submitError}
          tripMatch={tripMatch}
          onBack={navigateBack} // Step 1 does not have a back button
        />
      );
  }
};

export default TripReview;
